// Several files are joined into one video in the order shown, and a wrong order means
// processing again. These helpers read each file's recording time from its name, sort by it,
// and describe the gap between consecutive files so the order can be checked before upload.

const valid = (y, mo, d, h, mi, s) => y >= 2000 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31 &&
  h < 24 && mi < 60 && s < 60

// Camera and rink names, read as local time:
// 2026-09-25T135956 (rink cameras), 2026-09-25_13-59-56, 2026-09-25 13.59.56,
// VID_20260925_135956 / PXL_20260925_135956123 / 20260925-135956 (phones).
const TIME_PATTERNS = [
  /(\d{4})-(\d{2})-(\d{2})[T _-](\d{2})[-.:]?(\d{2})[-.:]?(\d{2})/,
  /(?:^|\D)(\d{4})(\d{2})(\d{2})[_T-](\d{2})(\d{2})(\d{2})/
]

export const recordingTime = name => {
  for (const pattern of TIME_PATTERNS) {
    const match = pattern.exec(name || '')
    if (!match) continue
    const [y, mo, d, h, mi, s] = match.slice(1, 7).map(Number)
    if (valid(y, mo, d, h, mi, s)) return new Date(y, mo - 1, d, h, mi, s).getTime()
  }
  return null
}

// GoPro splits one recording into chapters: GOPR1234 then GP011234, or GH011234 then GH021234.
export const goproChapter = name => {
  const legacy = /^GOPR(\d{4})\./i.exec(name || '')
  if (legacy) return Number(legacy[1]) * 100
  const chapter = /^G[HXP](\d{2})(\d{4})\./i.exec(name || '')
  return chapter ? Number(chapter[2]) * 100 + Number(chapter[1]) : null
}

const naturalName = (a, b) => a.file.name.localeCompare(b.file.name, undefined, { numeric: true, sensitivity: 'base' })

// The order key every file shares: recording time, else GoPro chapter, else none.
const keyKind = items => {
  if (items.every(item => recordingTime(item.file.name) !== null)) return 'time'
  if (items.every(item => goproChapter(item.file.name) !== null)) return 'chapter'
  return null
}

const keyOf = (item, kind) => kind === 'time' ? recordingTime(item.file.name) : goproChapter(item.file.name)

export const sortByRecording = items => {
  const kind = keyKind(items)
  return [...items].sort((a, b) => (kind ? keyOf(a, kind) - keyOf(b, kind) : 0) || naturalName(a, b))
}

// Whether the shown order can be confirmed from the names, and where it first goes wrong.
export const orderCheck = items => {
  const kind = keyKind(items)
  if (!kind) return { verifiable: false, inOrder: false, outOfOrderAt: null }
  const index = items.findIndex((item, i) => i > 0 && keyOf(item, kind) < keyOf(items[i - 1], kind))
  return { verifiable: true, inOrder: index === -1, outOfOrderAt: index === -1 ? null : index }
}

// Seconds between the end of each file and the start of the next (negative when they overlap);
// null when a recording time or a length is unknown.
export const gapsBetween = items => items.slice(0, -1).map((item, i) => {
  const start = recordingTime(item.file.name)
  const next = recordingTime(items[i + 1].file.name)
  if (start === null || next === null || !(item.durationSeconds > 0)) return null
  return (next - start) / 1000 - item.durationSeconds
})

export const formatDuration = seconds => {
  const total = Math.round(Math.abs(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = String(total % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`
}

// Short wording for the gap after a file; recordings within 5 seconds read as continuous.
export const describeGap = gap => {
  if (gap === null || gap === undefined) return null
  if (Math.abs(gap) <= 5) return { text: 'Continues into the next file', severity: 'success' }
  if (gap < 0) return { text: `Overlaps the next file by ${formatDuration(gap)}`, severity: 'warning' }
  return { text: `${formatDuration(gap)} gap before the next file`, severity: gap > 30 * 60 ? 'warning' : 'info' }
}

// The browser reads a file's length from its metadata; unknown (null) when it can't decode it.
export const readDuration = (file, timeoutMs = 15000) => new Promise(resolve => {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return resolve(null)
  const video = document.createElement('video')
  const url = URL.createObjectURL(file)
  let done = false
  const finish = value => {
    if (done) return
    done = true
    clearTimeout(timer)
    video.removeAttribute('src')
    URL.revokeObjectURL(url)
    resolve(Number.isFinite(value) && value > 0 ? value : null)
  }
  const timer = setTimeout(() => finish(null), timeoutMs)
  video.preload = 'metadata'
  video.onloadedmetadata = () => finish(video.duration)
  video.onerror = () => finish(null)
  video.src = url
})
